import * as React from "react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva } from "class-variance-authority"
import { X } from "lucide-react"
import { motion } from "framer-motion"
import { FluentEmoji } from '@lobehub/fluent-emoji'
import { cn } from "../../lib/utils"

// Toast Provider
const ToastProvider = ToastPrimitives.Provider

// Toast Viewport - 定义Toast显示的位置
const ToastViewport = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "fixed top-4 sm:top-8 left-1/2 transform -translate-x-1/2 z-[100]",
      "flex max-h-screen w-[calc(100vw-2rem)] sm:w-full flex-col gap-3",
      "pointer-events-none px-4 sm:px-0",
      className
    )}
    style={{ maxWidth: "420px" }}
    {...props}
  />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

// Toast样式变体
const toastVariants = cva(
  "pointer-events-auto group relative flex w-full items-center gap-3 sm:gap-4 overflow-hidden rounded-2xl sm:rounded-3xl p-4 sm:p-5 shadow-xl sm:shadow-2xl transition-all duration-500 backdrop-blur-3xl touch-pan-x",
  {
    variants: {
      variant: {
        default: "bg-gradient-to-r from-white/95 via-gray-50/95 to-white/95 dark:from-gray-900/95 dark:via-gray-800/95 dark:to-gray-900/95 text-gray-900 dark:text-white border border-gray-200/30 dark:border-gray-700/30",
        success: "bg-gradient-to-r from-white/95 via-green-50/20 to-white/95 dark:from-gray-900/95 dark:via-green-950/20 dark:to-gray-900/95 text-gray-900 dark:text-white border border-green-200/30 dark:border-green-800/30",
        destructive: "bg-gradient-to-r from-white/95 via-red-50/20 to-white/95 dark:from-gray-900/95 dark:via-red-950/20 dark:to-gray-900/95 text-gray-900 dark:text-white border border-red-200/30 dark:border-red-800/30",
        warning: "bg-gradient-to-r from-white/95 via-amber-50/30 to-white/95 dark:from-gray-900/95 dark:via-amber-950/30 dark:to-gray-900/95 text-gray-900 dark:text-white border border-amber-200/30 dark:border-amber-800/30",
        info: "bg-gradient-to-r from-white/95 via-blue-50/20 to-white/95 dark:from-gray-900/95 dark:via-blue-950/20 dark:to-gray-900/95 text-gray-900 dark:text-white border border-blue-200/30 dark:border-blue-800/30",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

// Emoji配置
const toastEmojiVariants = {
  default: "😊",
  success: "😁", 
  destructive: "🥶",
  warning: "😲",
  info: "🧐",
}

// Toast主组件
const Toast = React.forwardRef(({ className, variant, children, ...props }, ref) => {
  const emoji = toastEmojiVariants[variant] || toastEmojiVariants.default

  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    >
      {/* 装饰层 */}
      <div className="absolute inset-0 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none" />
      
      {/* Emoji图标 */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ 
          type: "spring", 
          stiffness: 260, 
          damping: 20,
          delay: 0.1 
        }}
        className="flex-shrink-0 relative z-10"
      >
        <div className="scale-90 sm:scale-100">
          <FluentEmoji 
            emoji={emoji} 
            type="anim" 
            size={32}
            cdn="unpkg"
          />
        </div>
      </motion.div>

      {/* 内容 */}
      <div className="flex-1 min-w-0 space-y-0.5 relative z-10">
        {children}
      </div>

      {/* 装饰效果 */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none" />
      <div className="absolute inset-0 rounded-2xl sm:rounded-3xl ring-1 ring-inset ring-white/10 dark:ring-white/5 pointer-events-none" />
      
      {/* 进度条 */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 4, ease: "linear" }}
        className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full origin-left"
      />
    </ToastPrimitives.Root>
  )
})
Toast.displayName = ToastPrimitives.Root.displayName

// Toast操作按钮
const ToastAction = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-7 sm:h-8 shrink-0 items-center justify-center",
      "rounded-xl sm:rounded-2xl border-0 bg-black/5 dark:bg-white/5",
      "px-2.5 sm:px-3 text-xs sm:text-sm font-medium",
      "text-gray-900 dark:text-gray-100",
      "transition-all duration-200 hover:bg-black/10 dark:hover:bg-white/10",
      "hover:scale-105 focus:outline-none focus:ring-2",
      "focus:ring-gray-400/50 dark:focus:ring-gray-600/50",
      "disabled:pointer-events-none disabled:opacity-50",
      className
    )}
    {...props}
  />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

// Toast关闭按钮
const ToastClose = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-2 sm:right-3 top-2 sm:top-3",
      "rounded-lg sm:rounded-xl p-1.5 sm:p-2",
      "text-gray-500 dark:text-gray-400",
      "opacity-0 transition-all duration-200",
      "hover:text-gray-700 dark:hover:text-gray-200",
      "hover:bg-gray-100/50 dark:hover:bg-gray-800/50",
      "hover:scale-105 focus:opacity-100 focus:outline-none",
      "focus:ring-2 focus:ring-gray-400/30 dark:focus:ring-gray-600/30",
      "group-hover:opacity-100 active:scale-95",
      className
    )}
    toast-close=""
    {...props}
  >
    <motion.div
      whileHover={{ rotate: 90 }}
      whileTap={{ scale: 0.9 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
    >
      <X className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
    </motion.div>
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

// Toast标题
const ToastTitle = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn(
      "text-sm sm:text-[15px] font-semibold leading-tight",
      "text-gray-900 dark:text-white",
      className
    )}
    {...props}
  />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

// Toast描述
const ToastDescription = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn(
      "text-xs sm:text-[13px] leading-relaxed",
      "text-gray-700 dark:text-gray-300 opacity-90 line-clamp-2",
      className
    )}
    {...props}
  />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

export {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
} 